import { action, computed, makeObservable, observable, runInAction } from "mobx"

import type { IStorageBackend } from "@/domains/common/storage"
import type { IEventBus } from "@/domains/events"

import type { IFeatureFlagsStore } from "./interfaces"
import type { FeatureFlagId } from "./types"
import { FeatureFlagDisabledEvent, FeatureFlagEnabledEvent } from "./events"
import { FEATURE_FLAG_REGISTRY } from "./types"

const STORAGE_KEY = "feature-flags"

export class FeatureFlagsStore implements IFeatureFlagsStore {
  // Observable map: flagId -> enabled state
  // Only stores user overrides (flags they've explicitly toggled)
  // Flags not in this map use their default from registry
  flagStates = new Map<FeatureFlagId, boolean>()

  constructor(
    private storage: IStorageBackend,
    private eventBus?: IEventBus,
  ) {
    makeObservable(this, {
      flagStates: observable,
      allFlags: computed,
      toggleFlag: action,
      resetFlag: action,
      resetAllFlags: action,
    })

    // Load persisted flags from localStorage
    this.initialize()
  }

  initialize(): void {
    runInAction(() => {
      this.initializeFromStorage()

      // Publish events for flags that are enabled at startup
      // This ensures orchestrators react even if flag was enabled before app load
      FEATURE_FLAG_REGISTRY.forEach((def) => {
        const isEnabled = this.isEnabled(def.id)
        if (isEnabled) {
          this.eventBus?.publish(
            new FeatureFlagEnabledEvent({
              flagId: def.id,
            }),
          )
        }
      })
    })
  }

  // Check if a flag is enabled
  isEnabled(flagId: FeatureFlagId): boolean {
    // Check user's persisted preference (localStorage)
    if (this.flagStates.has(flagId)) {
      return this.flagStates.get(flagId) ?? false
    }

    // Fall back to default from registry
    const definition = FEATURE_FLAG_REGISTRY.find((f) => f.id === flagId)
    return definition?.defaultEnabled ?? false
  }

  // Computed: Get all flag states
  get allFlags(): Map<FeatureFlagId, boolean> {
    const allFlags = new Map<FeatureFlagId, boolean>()
    FEATURE_FLAG_REGISTRY.forEach((def) => {
      allFlags.set(def.id, this.isEnabled(def.id))
    })
    return allFlags
  }

  getAllFlags(): Map<FeatureFlagId, boolean> {
    return this.allFlags
  }

  toggleFlag(flagId: FeatureFlagId): void {
    const currentState = this.isEnabled(flagId)
    const newState = !currentState

    // Persist to localStorage immediately
    this.flagStates.set(flagId, newState)
    this.saveToStorage()

    // Publish explicit event based on new state
    if (newState) {
      this.eventBus?.publish(
        new FeatureFlagEnabledEvent({
          flagId,
        }),
      )
    } else {
      this.eventBus?.publish(
        new FeatureFlagDisabledEvent({
          flagId,
        }),
      )
    }
  }

  resetFlag(flagId: FeatureFlagId): void {
    // Remove from persisted state (will fall back to default)
    this.flagStates.delete(flagId)
    this.saveToStorage()

    // Publish event based on default state
    const definition = FEATURE_FLAG_REGISTRY.find((f) => f.id === flagId)
    const defaultState = definition?.defaultEnabled ?? false

    if (defaultState) {
      this.eventBus?.publish(
        new FeatureFlagEnabledEvent({
          flagId,
        }),
      )
    } else {
      this.eventBus?.publish(
        new FeatureFlagDisabledEvent({
          flagId,
        }),
      )
    }
  }

  resetAllFlags(): void {
    // Clear all persisted states
    this.flagStates.clear()
    this.saveToStorage()

    // Publish events for all flags based on their defaults
    FEATURE_FLAG_REGISTRY.forEach((def) => {
      if (def.defaultEnabled) {
        this.eventBus?.publish(
          new FeatureFlagEnabledEvent({
            flagId: def.id,
          }),
        )
      } else {
        this.eventBus?.publish(
          new FeatureFlagDisabledEvent({
            flagId: def.id,
          }),
        )
      }
    })
  }

  private initializeFromStorage(): void {
    const stored = this.storage.getItem(STORAGE_KEY)
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as Record<FeatureFlagId, boolean>
        Object.entries(parsed).forEach(([flagId, enabled]) => {
          // Only restore flags that exist in registry
          if (FEATURE_FLAG_REGISTRY.some((f) => f.id === flagId)) {
            this.flagStates.set(flagId, enabled)
          }
        })
      } catch {
        // Invalid JSON, ignore
      }
    }
  }

  private saveToStorage(): void {
    // Persist all user overrides to localStorage
    // Only store flags that have been explicitly toggled (not defaults)
    const serialized = JSON.stringify(Object.fromEntries(this.flagStates))
    this.storage.setItem(STORAGE_KEY, serialized)
  }
}
